// The MCP Server page's AI tools and words: live's (`colortv-web/src/pages/mcp-server/
// constants.ts`), ours where our sign-in differs (spec platform/mcp-server, decision 12): a tool
// signs in with an access token of this team, never a URL of its own. Claude.ai and ChatGPT can
// send no token: they sign in by OAuth, on Created.ai's consent (spec platform/mcp-oauth), and
// their cards are Coming soon while that is off. Their steps are each client's own, from its
// current help pages (decision 42).
import claudeActive from "./assets/claude-active.svg";
import claude from "./assets/claude.svg";
import openAiActiveDark from "./assets/openai-active-dark.svg";
import openAiActive from "./assets/openai-active.svg";
import openAi from "./assets/openai.svg";

export type ClientId = "claude-code" | "claude-ai" | "chatgpt" | "codex";

export interface Client {
  id: ClientId;
  label: string;
  where: "Terminal" | "Web";
  icon: string;
  activeIcon: string;
  /** The active icon on a dark scheme, where live has one. */
  activeIconDark?: string;
  /** How it signs in: an access token made on this page, or OAuth, whose card is Coming soon
   *  while `features.mcp_oauth` is off. */
  signIn: "token" | "oauth";
}

export const CLIENTS: readonly Client[] = [
  {
    id: "claude-code",
    label: "Claude Code",
    where: "Terminal",
    icon: claude,
    activeIcon: claudeActive,
    signIn: "token",
  },
  {
    id: "claude-ai",
    label: "Claude.ai",
    where: "Web",
    icon: claude,
    activeIcon: claudeActive,
    signIn: "oauth",
  },
  {
    id: "chatgpt",
    label: "ChatGPT",
    where: "Web",
    icon: openAi,
    activeIcon: openAiActive,
    activeIconDark: openAiActiveDark,
    signIn: "oauth",
  },
  {
    id: "codex",
    label: "Codex",
    where: "Terminal",
    icon: openAi,
    activeIcon: openAiActive,
    activeIconDark: openAiActiveDark,
    signIn: "token",
  },
];

/** What a command shows until a token is made. */
export const TOKEN_PLACEHOLDER = "<your token>";
/** The variable Codex reads the token from, so the token stays out of its config file. */
export const TOKEN_VARIABLE = "CREATED_AI_TOKEN";
/** A token for an AI tool lasts the longest a token may (identity's 1 to 90 days). */
export const TOKEN_DAYS = 90;

export const SUCCESS_NOTE = "Signed in as you, in this team: the token is the only login step";
/** An OAuth card's: the team is picked on Created.ai, and nothing is copied but the URL. */
export const OAUTH_NOTE = "Signed in as you, in the team you pick: there is no token to copy";
export const CONNECTIONS_NOTE =
  "Settings, Access tokens lists each app you connect, and Revoke ends it. It ends by itself after 90 days.";

export const STEPS = {
  makeToken: (label: string) =>
    `Make an access token for ${label}. It shows once, here; Settings, Access tokens, revokes it.`,
  claudeCode: {
    runInTerminal: "Run this in your terminal:",
    confirmConnected: "Run /mcp to confirm created-ai is connected.",
  },
  codex: {
    addServer: "Add the server to ~/.codex/config.toml:",
    setToken: `Set ${TOKEN_VARIABLE} to the token where Codex runs:`,
    restart: "Restart Codex — the created-ai tools load automatically.",
  },
  // Claude's "Add a connector that isn't in the directory" (claude.com/docs, read 2026-11-04).
  claudeAi: [
    "In Claude.ai, open Customize, then Connectors, and click Add custom connector.",
    "Name it Created.ai, paste the server URL and click Add. If Claude asks how to sign in, pick Sign in now and Use Claude's published identity.",
    "Click Connect. Created.ai opens: pick the team and what Claude may do, then Allow.",
    "In a chat, select +, then Connectors, and turn Created.ai on.",
  ],
  claudeAiTeams:
    "On a Team or Enterprise plan, an Owner adds the connector in Organization settings, Connectors, and each member clicks Connect in Customize, Connectors.",
  // OpenAI's "Add custom MCP server" (developers.openai.com, read 2026-11-04).
  chatgpt: [
    "In ChatGPT on the web, open Plugins, select +, then Add custom MCP server.",
    "Name it Created.ai and paste the server URL under Connection.",
    "For Authentication pick OAuth, with its client metadata document (CIMD) where it asks, and no client ID or secret. Select I understand and want to continue, then Create as a plugin.",
    "Install the plugin and sign in when ChatGPT asks. Created.ai opens: pick the team and what ChatGPT may do, then Allow.",
    "In a new chat, type @ and pick Created.ai.",
  ],
} as const;

export const claudeCodeCommand = (url: string, token: string) =>
  `claude mcp add --transport http created-ai ${url} --header "Authorization: Bearer ${token}"`;

export const codexConfig = (url: string) =>
  `[mcp_servers.created-ai]\nurl = "${url}"\nbearer_token_env_var = "${TOKEN_VARIABLE}"`;

export const codexToken = (token: string) => `export ${TOKEN_VARIABLE}=${token}`;

// Live's suggestions, word for word.
export const PROMPTS = [
  "What products do we have? Pick one and pitch a campaign",
  "Describe what you’d like to design today",
  "Start from an ad idea — brainstorm 5 directions",
  "Generate 3 product shots, look at them, keep the best",
] as const;
